/** Matches BasicInfoStep description `TextField` (`multiline`, `minRows={5}`). */
export const EVENT_DESCRIPTION_MIN_ROWS = 5;

/** Minimum display height aligned with that field (~5 lines). */
export const EVENT_DESCRIPTION_MIN_HEIGHT = 140;

/** Preserve textarea line breaks when rendering description as plain text. */
export const PRESERVE_LINE_BREAKS_SX = { whiteSpace: 'pre-wrap' };

/** API/DB may store escaped newlines as literal `\n`; convert for display and editing. */
export const normalizeMultilineText = (value) => {
  if (value == null || value === '') return '';
  return String(value).replace(/\\n/g, '\n');
};

/** Normalize multiline text for read-only display (e.g. dish / event descriptions). */
export const formatMultilineDisplayText = (value) => normalizeMultilineText(value).trim();

/** Textarea value: escaped newlines, unified line endings, optional max length. */
export const normalizeFormMultilineField = (value, maxLength) => {
  let text = normalizeMultilineText(value);
  text = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  if (typeof maxLength === 'number' && maxLength >= 0 && text.length > maxLength) {
    return text.slice(0, maxLength);
  }

  return text;
};

/** Read-only event description: capped height with scroll (~5 input lines). */
export const SCROLLABLE_DESCRIPTION_SX = {
  ...PRESERVE_LINE_BREAKS_SX,
  maxHeight: EVENT_DESCRIPTION_MIN_HEIGHT,
  overflowY: 'auto',
};

/** Read-only menu items list: capped height with scroll. */
export const MENU_LIST_DISPLAY_MAX_HEIGHT = 320;

export const SCROLLABLE_MENU_LIST_SX = {
  maxHeight: MENU_LIST_DISPLAY_MAX_HEIGHT,
  overflowY: 'auto',
  pr: 0.5,
};

/** General event description (Basic Info step). */
export const EVENT_DESCRIPTION_MAX = 1000;

/** Short / wingman description (Basic Info step). */
export const EVENT_SHORT_DESCRIPTION_MAX = 150;

/** UI course label → API key in menuItems JSON. */
export const COURSE_API_KEYS = {
  Starter: 'starter',
  'Main Course': 'main',
  Dessert: 'desert',
};

export const COVER_IMAGE_FIELD = 'coverImage';

/** POST /admin/regenerate-cover-image multipart field name. */
export const REGENERATE_COVER_IMAGE_FIELD = 'eventCoverImage';

export const COVER_IMAGE_ALLOWED_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png']);

export const COVER_IMAGE_ACCEPT = 'image/jpeg,image/png,.jpg,.jpeg,.png';

/** Square cover preview on Basic Info and Confirmation steps. */
export const COVER_IMAGE_SQUARE_WIDTH = '50%';

/** Maximum cover image upload size (Basic Info step). */
export const COVER_IMAGE_MAX_BYTES = 10 * 1024 * 1024;
export const COVER_IMAGE_SIZE_HINT = 'JPG, JPEG or PNG · Maximum 10 MB';

/** POST /admin/regenerate-cover-image may take up to 2 minutes. */
export const COVER_IMAGE_OPTIMIZATION_TIMEOUT_MS = 2 * 60 * 1000;

export const MIN_DISH_IMAGE_SIZE = 400;
export const DISH_IMAGE_DIMENSION_HINT = `Minimum ${MIN_DISH_IMAGE_SIZE} × ${MIN_DISH_IMAGE_SIZE}px (square)`;
export const DISH_IMAGE_FORMAT_HINT = 'PNG, JPG or JPEG only';
export const DISH_IMAGE_ACCEPT = 'image/png,image/jpeg,.png,.jpg,.jpeg';
/** On-screen preview size; uploads validated at minimum MIN_DISH_IMAGE_SIZE. */
export const DISH_IMAGE_PREVIEW_SIZE = 120;

export const MENU_ATTACH_FILE_FIELD = 'menuItemsAttachment';
export const MENU_PREFILL_FILE_FIELD = 'menuPrefillFile';
/** Multipart field name for POST /admin/extract-menu-items */
export const MENU_EXTRACT_FILE_FIELD = 'file';

/** POST /admin/extract-event-details JSON `prompt` max length (matches backend). */
export const EXTRACT_EVENT_DETAILS_PROMPT_MAX = 20000;

/** POST /admin/extract-event-details may take up to 2 minutes. */
export const EXTRACT_EVENT_DETAILS_TIMEOUT_MS = 2 * 60 * 1000;

export const MENU_PREFILL_FILE_ACCEPT = 'application/pdf,image/png,image/jpeg,.pdf,.png,.jpg,.jpeg';
export const MENU_PREFILL_FILE_FORMAT_HINT = 'PDF or image file (PNG, JPG or JPEG) only';

export const isMenuPrefillFileAllowed = (file) => {
  if (!(file instanceof File)) return false;
  if (file.type === 'application/pdf') return true;
  if (file.type === 'image/png' || file.type === 'image/jpeg') return true;
  return /\.(pdf|png|jpe?g)$/i.test(file.name);
};

/** e.g. buildMenuDishImageFieldName('starter', 1) → 'menuImage_starter_1' */
export const buildMenuDishImageFieldName = (courseKey, index) => `menuImage_${courseKey}_${index}`;

export const getMenuApiKeyForCourse = (courseLabel) => COURSE_API_KEYS[courseLabel];
